import React, { useState, useEffect, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useDispatch } from 'react-redux';
import { handlePostRequest } from '../../services/PostTemplate';
import { handlePutRequest } from '../../services/PutTemplate';
import { handleGetRequest } from '../../services/GetTemplate';
import { handleDeleteRequest } from '../../services/DeleteTemplate';
import Loader from '../../components/loader';
import './AddEditRoutes.css';
import { toast } from 'react-toastify';


function AddEditRoutes({ onHideSidebar, isEditing, editData, fetchUsersData }) {
    const [loading, setLoading] = useState(false);
    const [stops, setStops] = useState([]);
    const [stopDialogVisible, setStopDialogVisible] = useState(false);
    const [currentStop, setCurrentStop] = useState(null);
    const [isEditingStop, setIsEditingStop] = useState(false);
    const [tempStops, setTempStops] = useState([]);
    const dispatch = useDispatch();

    const statusOptions = [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
    ];

    const stopsRequest = tempStops?.map((item) => {
        return item?.stopName;
    });

    const routeSchema = Yup.object().shape({
        routeNumber: Yup.string().required('Route number is required'),
        routeFrom: Yup.string().required('Starting point is required'),
        routeTo: Yup.string().required('Destination is required'),
        vehicles: Yup.string().required('Vehicles are required'),
        distanceInKm: Yup.number()
            .transform((value) => (isNaN(value) ? undefined : value))
            .required('Distance is required')
            .min(0.1, 'Distance must be greater than 0')
            .typeError('Distance must be a valid number'),
        fare: Yup.number()
            .transform((value) => (isNaN(value) ? undefined : value))
            .required('Fare is required')
            .min(1, 'Fare must be at least 1')
            .typeError('Fare must be a number'),
        isActive: Yup.boolean().required('Status is required')
    });

    const stopSchema = Yup.object().shape({
        stopName: Yup.string().required('Stop name is required'),
        isActive: Yup.boolean().required('Status is required')
    });

    const formik = useFormik({
        validationSchema: routeSchema,
        initialValues: {
            routeNumber: editData?.routeNumber || '',
            routeFrom: editData?.routeFrom || '',
            routeTo: editData?.routeTo || '',
            vehicles: editData?.vehicles || '',
            distanceInKm: editData?.distanceInKm,
            fare: editData?.fare, // Default to 1 to match validation requirement
            isActive: editData?.isActive ?? true,
            routeStops: editData?.routeStops || []
        },
        onSubmit: async (values) => {
            if (isEditing) {
                try {
                    setLoading(true);
                    const obj = {
                        id: editData.id,
                        ...values
                    };
                    const response = await dispatch(handlePutRequest(obj, '/api/route'));
                    if (response?.success === true) {
                        fetchUsersData()
                        onHideSidebar();
                    }
                } catch (error) {
                    toast.error(error);
                } finally {
                    setLoading(false);
                }
            } else {
                try {
                    setLoading(true);
                    // First create the route
                    const routeResponse = await dispatch(
                        handlePostRequest(
                            {
                                routeNumber: values.routeNumber,
                                routeFrom: values.routeFrom,
                                routeTo: values.routeTo,
                                vehicles: values.vehicles,
                                routeStops: stopsRequest,
                                distanceInKm: values.distanceInKm,
                                fare: values.fare,
                                isActive: values.isActive
                            },
                            '/api/route'
                            ,false,false
                        )
                    );

                    if (routeResponse?.success === true) {
                        // Then add all stops for this route
                        // const stopPromises = tempStops.map((stop) => dispatch(handlePostRequest(null, `/api/Route/AddStop?RouteNumber=${values.routeNumber}&StopName=${stop.stopName}`)));

                        // await Promise.all(stopPromises);
                        onHideSidebar();
                        fetchUsersData()
                        toast.success(routeResponse?.message || 'Route and stops added successfully');
                    }
                } catch (error) {
                    console.error('Error:', error);
                    toast.error(error?.response?.data?.message || 'Failed to add route and stops');
                } finally {
                    setLoading(false);
                }
            }
        }
    });

    const stopFormik = useFormik({
        initialValues: {
            id: null,
            stopName: '',
            isActive: true
        },
        validationSchema: stopSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                if (isEditing) {
                    if (isEditingStop && currentStop) {
                        // Update existing stop
                        const obj = {
                            id: values.id, // Use the ID from the form values
                            newStopName: values.stopName,
                            isActive: values.isActive
                        };
                        const response = await dispatch(handlePutRequest(obj, '/api/route/stop'));
                        if (response?.success) {
                            fetchStops();
                            toast.success(response?.message || 'Stop updated successfully');
                            setStopDialogVisible(false);
                            stopFormik.resetForm();
                        }
                    } else {
                        // Add new stop to existing route
                        const response = await dispatch(handlePostRequest({ routeNumber: formik.values.routeNumber, stopName: values.stopName }, '/api/route/stop',false,false));
                        if (response?.success) {
                            fetchStops();
                            toast.success(response?.message || 'Stop added successfully');
                        }
                        setStopDialogVisible(false);
                        stopFormik.resetForm();
                    }
                } else {
                    // Add new stop to temp stops during route creation
                    const newStop = {
                        id: Date.now(), // temporary ID
                        stopName: values.stopName,
                        isActive: values.isActive
                    };
                    setTempStops([...tempStops, newStop]);
                    setStopDialogVisible(false);
                    stopFormik.resetForm();
                }
            } catch (error) {
                console.error('Error:', error);
            } finally {
                setLoading(false);
            }
        }
    });

    const fetchStops = useCallback(async () => {
        setLoading(true);
        const response = await handleGetRequest(`/api/route/stop/${editData?.routeNumber}`);
        if (response?.success === true) {
            console.log('Fetched Stops:', response?.data);
            setStops(response?.data);
        }
        setLoading(false);
    }, [editData, setLoading]);

    useEffect(() => {
        if (isEditing && editData) {
            fetchStops();
        }
    }, [isEditing, editData, fetchStops]);

    const openNewStopDialog = (e) => {
        e.preventDefault();
        setIsEditingStop(false);
        setCurrentStop(null);
        stopFormik.resetForm();
        setStopDialogVisible(true);
    };

    const openEditStopDialog = (stop, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        setIsEditingStop(true);
        setCurrentStop({...stop}); // Store complete stop object
        stopFormik.setValues({
            id: stop.id, // Include stop ID for PUT request
            stopName: stop.stopName,
            isActive: stop.isActive
        });
        setStopDialogVisible(true);
    };

    const confirmDeleteStop = (stop, event) => {
        // Stop event propagation to prevent dialog from closing immediately
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        
        confirmDialog({
            message: 'Are you sure you want to delete this stop?',
            header: 'Confirm',
            icon: 'pi pi-exclamation-triangle',
            accept: () => deleteStop(stop)
        });
    };

  const deleteStop = async (stop) => {
    try {
        setLoading(true);
        if (isEditing) {
            const response = await dispatch(handleDeleteRequest(`/api/route/stop/${stop.id}`, false, false));
            if (response?.success) {
                setStops(prevStops => prevStops.filter(s => s.id !== stop.id));
                toast.success(response?.message || 'Stop deleted successfully');
            }
        } else {
            setTempStops(tempStops.filter((s) => s.id !== stop.id));
            toast.success('Stop removed successfully');
        }
    } catch (error) {
        console.error('Error:', error);
    } finally {
        setLoading(false);
    }
};

    const stopStatusBodyTemplate = (rowData) => {
        return (
            <div className="flex justify-content-center">
                <span className={`status-badge ${rowData.isActive ? 'status-active' : 'status-inactive'}`}>
                    {rowData.isActive ? 'Active' : 'Inactive'}
                </span>
            </div>
        );
    };

    const stopActionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex justify-content-center">
                <Button 
                    icon="pi pi-pencil" 
                    className="p-button-rounded p-button-primary p-button-text mr-2" 
                    onClick={(e) => openEditStopDialog(rowData, e)} 
                />
                <Button 
                    icon="pi pi-trash" 
                    className="p-button-rounded p-button-danger p-button-text" 
                    onClick={(e) => confirmDeleteStop(rowData, e)}
                />
            </div>
        );
    };

    const renderStopsSection = () => {
        const currentStops = isEditing ? stops : tempStops;

        return (
            <div className="form-group mb-4">
                <div className="flex justify-content-between align-items-center mb-2">
                    <label className="form-label">Route Stops</label>
                    <Button 
                        label="Add Stop" 
                        icon="pi pi-plus" 
                        className="add-stop-button" 
                        onClick={(e) => openNewStopDialog(e)}
                    />
                </div>

                {currentStops.length > 0 ? (
                    <div className="card p-0" style={{marginBottom : '120px'}}>
                        <DataTable value={currentStops} className="p-datatable-sm" emptyMessage="No stops found." resizableColumns={false} responsiveLayout="scroll" style={{ width: '100%' }}>
                            <Column field="stopName" header="Stop Name" style={{ width: '50%', textAlign: 'left' }} headerStyle={{ textAlign: 'left' }} />
                            {isEditing && <Column field="isActive" header="Status" body={stopStatusBodyTemplate} style={{ width: '25%', textAlign: 'left' }} headerStyle={{ textAlign: 'center' }} />}
                            <Column body={stopActionBodyTemplate} header="Actions" style={{ width: '25%', textAlign: 'left' }} headerStyle={{ textAlign: 'center' }} />
                        </DataTable>
                    </div>
                ) : (
                    <div className="p-3 text-center text-gray-500 border-1 border-gray-200 border-round">No stops added yet</div>
                )}
            </div>
        );
    };

    return (
        <>
            <ConfirmDialog />
            <div className="sidebar-header">
                <div className="header-content">
                    <h2>{isEditing ? 'Edit Route' : 'Add Route'}</h2>
                    <Button icon="pi pi-times" onClick={onHideSidebar} className="p-button-rounded p-button-danger p-button-text p-button-sm" style={{ fontSize: '1rem' }} />
                </div>
            </div>

            <div className="p-4 flex flex-column" style={{ height: 'calc(100vh - 155px)', overflow: 'hidden', maxHeight: '100%' }}>
                <form id="routeForm" onSubmit={formik.handleSubmit} className="flex flex-column h-full relative">
                    <div className="flex-grow-1 overflow-y-auto pr-2" style={{ paddingBottom: '100px' }}>
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="form-group mb-3">
                                    <label className="form-label">Route Number</label>
                                    <InputText
                                        id="routeNumber"
                                        name="routeNumber"
                                        value={formik.values.routeNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`Input__Round_Route ${formik.touched.routeNumber && formik.errors.routeNumber ? 'is-invalid' : ''}`}
                                        placeholder="Enter route number"
                                        disabled={isEditing}
                                    />
                                    {formik.touched.routeNumber && formik.errors.routeNumber && <div className="invalid-feedback">{formik.errors.routeNumber}</div>}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="form-group mb-3">
                                    <label className="form-label">From</label>
                                    <InputText
                                        id="routeFrom"
                                        name="routeFrom"
                                        value={formik.values.routeFrom}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`Input__Round_Route ${formik.touched.routeFrom && formik.errors.routeFrom ? 'is-invalid' : ''}`}
                                        placeholder="Starting point"
                                    />
                                    {formik.touched.routeFrom && formik.errors.routeFrom && <div className="invalid-feedback">{formik.errors.routeFrom}</div>}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="form-group mb-3">
                                    <label className="form-label">To</label>
                                    <InputText
                                        id="routeTo"
                                        name="routeTo"
                                        value={formik.values.routeTo}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`Input__Round_Route ${formik.touched.routeTo && formik.errors.routeTo ? 'is-invalid' : ''}`}
                                        placeholder="Destination"
                                    />
                                    {formik.touched.routeTo && formik.errors.routeTo && <div className="invalid-feedback">{formik.errors.routeTo}</div>}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="form-group mb-3">
                                    <label className="form-label">Vehicles</label>
                                    <InputText
                                        id="vehicles"
                                        name="vehicles"
                                        value={formik.values.vehicles}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`Input__Round_Route ${formik.touched.vehicles && formik.errors.vehicles ? 'is-invalid' : ''}`}
                                        placeholder="Enter vehicles"
                                    />
                                    {formik.touched.vehicles && formik.errors.vehicles && <div className="invalid-feedback">{formik.errors.vehicles}</div>}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="form-group mb-3">
                                    <label className="form-label">Distance (km)</label>
                                    <InputNumber
                                        id="distanceInKm"
                                        name="distanceInKm"
                                        value={formik.values.distanceInKm}
                                        onValueChange={(e) => formik.setFieldValue('distanceInKm', e.value)}
                                        mode="decimal"
                                        min={0}
                                        minFractionDigits={0}
                                        maxFractionDigits={2}
                                        // suffix=" km"
                                        // showButtons
                                        // step={0.1}
                                        useGrouping={false}
                                        className={`Input__Round_Route ${formik.touched.distanceInKm && formik.errors.distanceInKm ? 'is-invalid' : ''}`}
                                        placeholder="Enter distance (e.g. 10.5)"
                                    />
                                    {formik.touched.distanceInKm && formik.errors.distanceInKm && <div className="invalid-feedback">{formik.errors.distanceInKm}</div>}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="form-group mb-3">
                                    <label className="form-label">Fare</label>
                                    <InputNumber
                                        id="fare"
                                        name="fare"
                                        value={formik.values.fare}
                                        onValueChange={(e) => formik.setFieldValue('fare', e.value)}
                                        mode="decimal" /* Changed from currency to decimal */
                                        min={1} /* Changed minimum value to 1 to match validation */
                                        minFractionDigits={0}
                                        maxFractionDigits={0}
                                        useGrouping={false}
                                        className={`Input__Round_Route ${formik.touched.fare && formik.errors.fare ? 'is-invalid' : ''}`}
                                        placeholder="Enter fare"
                                    />
                                    {formik.touched.fare && formik.errors.fare && <div className="invalid-feedback">{formik.errors.fare}</div>}
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="form-group mb-3">
                                    <label className="form-label">Status</label>
                                    <Dropdown
                                        id="isActive"
                                        name="isActive"
                                        options={statusOptions}
                                        value={formik.values.isActive}
                                        onChange={(e) => formik.setFieldValue('isActive', e.value)}
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder="Select status"
                                        className={`Dropdown__Round w-full ${formik.touched.isActive && formik.errors.isActive ? 'is-invalid' : 'Dropdown__Round w-full'}`}
                                    />
                                    {formik.touched.isActive && formik.errors.isActive && <div className="invalid-feedback">{formik.errors.isActive}</div>}
                                </div>
                            </div>
                        </div>

                        {renderStopsSection()}
                    </div>
                    
                    
                </form>
                <div className="mt-3 pt-3 border-top-1 border-300" 
                         style={{ 
                             position: 'absolute', 
                             bottom: 0, 
                             left: 0, 
                             right: 0, 
                             background: 'white', 
                             zIndex: 1000,
                             padding: '1rem 2rem',
                             boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                             width: '100%'
                         }}>
                        <div className="flex justify-content-center">
                            <Button 
                                type="submit" 
                                label={isEditing ? 'Update Route' : 'Add Route'} 
                                className="route-submit-button" 
                                disabled={loading} 
                                onClick={() => formik.handleSubmit()}
                            />
                        </div>
                    </div>
            </div>

            {/* Stop Dialog */}
            <Dialog 
                visible={stopDialogVisible} 
                style={{ width: '90%', maxWidth: '450px' }} 
                header={isEditingStop ? 'Edit Stop' : 'Add Stop'} 
                modal 
                className="p-fluid stop-dialog" 
                onHide={() => setStopDialogVisible(false)}
            >
                <form id="stopForm" onSubmit={stopFormik.handleSubmit}>
                    <div className="form-group">
                        <label className="form-label">Stop Name</label>
                        <InputText
                            id="stopName"
                            name="stopName"
                            value={stopFormik.values.stopName}
                            onChange={stopFormik.handleChange}
                            onBlur={stopFormik.handleBlur}
                            className={`form-control ${stopFormik.touched.stopName && stopFormik.errors.stopName ? 'is-invalid' : ''}`}
                            placeholder="Enter stop name"
                        />
                        {stopFormik.touched.stopName && stopFormik.errors.stopName && <div className="invalid-feedback">{stopFormik.errors.stopName}</div>}
                    </div>

                    {isEditing && (
                        <div className="form-group">
                            <label className="form-label">Status</label>
                            <Dropdown
                                id="isActive"
                                name="isActive"
                                options={statusOptions}
                                value={stopFormik.values.isActive}
                                onChange={(e) => stopFormik.setFieldValue('isActive', e.value)}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select status"
                                className={`${stopFormik.touched.isActive && stopFormik.errors.isActive ? 'is-invalid' : ''}`}
                            />
                            {stopFormik.touched.isActive && stopFormik.errors.isActive && <div className="invalid-feedback">{stopFormik.errors.isActive}</div>}
                        </div>
                    )}
                    
                    {/* Buttons moved into the form */}
                    <div className="flex justify-content-end mt-4" style={{ gap: '12px' }}>
                        <Button 
                            label="Cancel" 
                            icon="pi pi-times" 
                            className="p-button-text" 
                            onClick={() => setStopDialogVisible(false)} 
                            style={{ minWidth: '100px' }}
                            type="button"
                        />
                        <Button 
                            type="submit" 
                            label={isEditingStop ? 'Update' : 'Save'} 
                            icon="pi pi-check" 
                            className="p-button" 
                            style={{ 
                                backgroundColor: '#FF556A', 
                                border: 'none', 
                                minWidth: '100px',
                                borderRadius: '0.75rem'
                            }} 
                            disabled={loading} 
                        />
                    </div>
                </form>
            </Dialog>

            {loading && <Loader />}
        </>
    );
}

export default AddEditRoutes;
